import { CardState, CardData } from '../types/cardTypes';

export class CardStateService {
  /**
   * Get the appropriate route for a card state
   * Based on the WIX router logic
   */
  static getRouteForCardState(cardState: CardState, cardId: string): string {
    switch (cardState) {
      case CardState.ACTIVATED:
        return `/balance-display?cardId=${cardId}`;
      
      case CardState.GIFT_CARD:
        return `/new-customer?cardId=${cardId}&giftcard=true`;

      case CardState.NEW:
        return `/welcome?cardId=${cardId}`;
      
      case CardState.OWNED:
        return `/redirect-to-activation?cardId=${cardId}`;
      
      case CardState.CHECKED_OUT:
        return `/balance-display?cardId=${cardId}`;
      
      case CardState.READY_FOR_ACTIVATION:
        return `/redirect-to-activation?cardId=${cardId}`;
      
      case CardState.READY_FOR_PURCHASE:
        return `/redirect-to-banxa?cardId=${cardId}`;

      case CardState.EMAIL_VERIFIED:
        // Email verified: start KYC
        return `/kyc-verification?cardId=${cardId}`;

      case CardState.KYCPENDING:
        // Default: still show the Sumsub verification UI
        return `/kyc-verification?cardId=${cardId}`;

      case CardState.CARD_NOT_USABLE:
        return `/card-not-usable?cardId=${cardId}`;
      
      case CardState.FINAL_STEP:
        return `/final-step?cardId=${cardId}`;
      
      case CardState.TRY_AGAIN:
        return `/try-again?cardId=${cardId}`;
      
      case CardState.UNKNOWN:
      default:
        return `/error-page?cardId=${cardId}`;
    }
  }

  /**
   * Check if a route is allowed for a given card state
   */
  static isRouteAllowedForState(route: string, cardState: CardState): boolean {
    const allowedStates = CardStateService.getAllowedStatesForRoute(route);
    return allowedStates.includes(cardState);
  }

  /**
   * Get allowed states for a specific route
   */
  static getAllowedStatesForRoute(route: string): CardState[] {
    switch (route) {
      case '/welcome':
        return [CardState.NEW];
      
      case '/new-customer':
        return [CardState.NEW, CardState.GIFT_CARD];
      
      case '/purchase-crypto':
        return [CardState.OWNED, CardState.NEW];
      
      case '/balance-display':
        return [CardState.ACTIVATED, CardState.CHECKED_OUT];
      
      case '/redirect-to-activation':
        return [CardState.READY_FOR_ACTIVATION, CardState.OWNED];
      
      case '/redirect-to-banxa':
        return [CardState.READY_FOR_PURCHASE];
      
      case '/card-activation':
        return [CardState.READY_FOR_ACTIVATION, CardState.OWNED];
      
      case '/kyc-verification':
        return [CardState.EMAIL_VERIFIED, CardState.KYCPENDING];

      case '/kyc-in-process':
        return [CardState.KYCPENDING];

      case '/card-not-usable':
        return [CardState.CARD_NOT_USABLE];

      case '/final-step':
        return [CardState.FINAL_STEP];
      
      case '/try-again':
        return [CardState.TRY_AGAIN];
      
      case '/error-page':
        return [CardState.UNKNOWN];
      
      default:
        return [];
    }
  }

  /**
   * Validate card data and return appropriate error message
   */
  static validateCardData(cardData: CardData | null, cardId: string): { isValid: boolean; errorMessage?: string } {
    if (!cardData) {
      return {
        isValid: false,
        errorMessage: `No card found for ID: ${cardId}`
      };
    }

    if (cardData.state === CardState.UNKNOWN) {
      return {
        isValid: false,
        errorMessage: 'Card in unknown state'
      };
    }

    return { isValid: true };
  }

  /**
   * Get the default route for error handling
   */
  static getErrorRoute(errorMessage: string, cardId?: string): string {
    // Store error message in localStorage (equivalent to wixStorage.local.setItem)
    localStorage.setItem('errorMessage', errorMessage);
    const cardIdParam = cardId ? `?cardId=${cardId}` : '';
    return `/error-page${cardIdParam}`;
  }
} 